import {
  addDays,
  addHours,
  addMinutes,
  addMonths,
  addYears,
  endOfMonth,
  format,
  getDate,
  getDaysInMonth,
  getHours,
  getMinutes,
  getMonth,
  getYear,
  isAfter,
  isBefore,
  isEqual,
  isValid,
  parse,
  setDate,
  setHours,
  setMinutes,
  setMonth,
  setYear,
  startOfMonth,
  startOfWeek,
} from "date-fns";

type DateUnit = "day" | "date" | "month" | "year" | "hour";
type AddUnit = "day" | "d" | "month" | "year";

const TOKEN_REPLACEMENTS: Array<[RegExp, string]> = [
  [/YYYY/g, "yyyy"],
  [/YY/g, "yy"],
  [/DD/g, "dd"],
  [/D/g, "d"],
  [/A/g, "aa"],
  [/a/g, "aa"],
];

function convertFormatToken(input: string): string {
  let out = input;
  for (const [re, token] of TOKEN_REPLACEMENTS) {
    out = out.replace(re, token);
  }
  return out;
}

function coerceDate(
  value?: string | Date | CalendarDate | null,
  formatStr?: string,
): Date | undefined {
  if (value === null || value === undefined) return undefined;
  if (value instanceof CalendarDate) return value.toDate();
  if (value instanceof Date)
    return isValid(value) ? new Date(value) : undefined;
  if (typeof value === "string") {
    if (formatStr) {
      const parsed = parse(value, convertFormatToken(formatStr), new Date());
      if (isValid(parsed)) return parsed;
    }
    const raw = new Date(value);
    return isValid(raw) ? raw : undefined;
  }
  return undefined;
}

function compareByUnit(left: Date, right: Date, unit: DateUnit): number {
  const l = new Date(left);
  const r = new Date(right);
  if (unit === "year") {
    return getYear(l) - getYear(r);
  }
  if (unit === "month") {
    return getYear(l) === getYear(r) ?
        getMonth(l) - getMonth(r)
      : getYear(l) - getYear(r);
  }
  if (unit === "day" || unit === "date") {
    const ly = getYear(l),
      ry = getYear(r);
    if (ly !== ry) return ly - ry;
    const lm = getMonth(l),
      rm = getMonth(r);
    if (lm != rm) return lm - rm;
    return getDate(l) - getDate(r);
  }
  if (unit === "hour") {
    const dcmp = compareByUnit(l, r, "date");
    if (dcmp !== 0) return dcmp;
    return getHours(l) - getHours(r);
  }
  return l.getTime() - r.getTime();
}

export class CalendarDate {
  private value: Date;
  private language: string | undefined;

  constructor(date: Date, localization?: string) {
    this.value = new Date(date);
    this.language = localization;
  }

  clone(): CalendarDate {
    return new CalendarDate(this.value, this.language);
  }

  locale(localization?: string): CalendarDate {
    this.language = localization;
    return this;
  }

  toDate(): Date {
    return new Date(this.value);
  }

  year(): number;
  year(next: number): CalendarDate;
  year(next?: number): number | CalendarDate {
    if (next !== undefined) {
      this.value = setYear(this.value, next);
      return this;
    }
    return getYear(this.value);
  }

  month(): number;
  month(next: number): CalendarDate;
  month(next?: number): number | CalendarDate {
    if (next !== undefined) {
      this.value = setMonth(this.value, next);
      return this;
    }
    return getMonth(this.value);
  }

  date(): number;
  date(next: number): CalendarDate;
  date(next?: number): number | CalendarDate {
    if (next !== undefined) {
      this.value = setDate(this.value, next);
      return this;
    }
    return getDate(this.value);
  }

  hour(): number;
  hour(next: number): CalendarDate;
  hour(next?: number): number | CalendarDate {
    if (next !== undefined) {
      this.value = setHours(this.value, next);
      return this;
    }
    return getHours(this.value);
  }

  minute(): number;
  minute(next: number): CalendarDate;
  minute(next?: number): number | CalendarDate {
    if (next !== undefined) {
      this.value = setMinutes(this.value, next);
      return this;
    }
    return getMinutes(this.value);
  }

  minutes(): number {
    return getMinutes(this.value);
  }

  add(amount: number, unit: AddUnit): CalendarDate {
    if (unit === "month") this.value = addMonths(this.value, amount);
    if (unit === "year") this.value = addYears(this.value, amount);
    if (unit === "day" || unit === "d")
      this.value = addDays(this.value, amount);
    return this;
  }

  subtract(amount: number, unit: AddUnit): CalendarDate {
    return this.add(-amount, unit);
  }

  startOf(unit: "month" | "week"): CalendarDate {
    if (unit === "month") this.value = startOfMonth(this.value);
    if (unit === "week") this.value = startOfWeek(this.value);
    return this;
  }

  endOf(unit: "month"): CalendarDate {
    if (unit === "month") this.value = endOfMonth(this.value);
    return this;
  }

  isSame(other: CalendarDate, unit: DateUnit): boolean {
    return compareByUnit(this.value, other.toDate(), unit) === 0;
  }

  isBefore(other: CalendarDate, unit?: DateUnit): boolean {
    if (unit) return compareByUnit(this.value, other.toDate(), unit) < 0;
    return isBefore(this.value, other.toDate());
  }

  isAfter(other: CalendarDate, unit?: DateUnit): boolean {
    if (unit) return compareByUnit(this.value, other.toDate(), unit) > 0;
    return isAfter(this.value, other.toDate());
  }

  isSameOrBefore(other: CalendarDate, unit: DateUnit): boolean {
    return compareByUnit(this.value, other.toDate(), unit) <= 0;
  }

  isSameOrAfter(other: CalendarDate, unit: DateUnit): boolean {
    return compareByUnit(this.value, other.toDate(), unit) >= 0;
  }

  isBetween(start: CalendarDate, end: CalendarDate): boolean {
    return (
      this.value.getTime() > start.toDate().getTime() &&
      this.value.getTime() < end.toDate().getTime()
    );
  }

  daysInMonth(): number {
    return getDaysInMonth(this.value);
  }

  format(formatString: string): string {
    return format(this.value, convertFormatToken(formatString));
  }

  isValid(): boolean {
    return isValid(this.value);
  }
}

export type MomentLike = CalendarDate;

export function createDate(
  value?: string | Date | CalendarDate | null,
  formatStr?: string,
  localization?: string,
): CalendarDate | undefined {
  const parsed = coerceDate(value, formatStr);
  if (!parsed) return undefined;
  return new CalendarDate(parsed, localization);
}

export function fromParts(
  parts: {
    year?: number | undefined;
    month?: number | undefined;
    date?: number | undefined;
    hour?: number | undefined;
    minute?: number | undefined;
  },
  localization?: string,
): CalendarDate {
  const now = new Date();
  let d = new Date(
    parts.year ?? getYear(now),
    parts.month ?? getMonth(now),
    parts.date ?? getDate(now),
    parts.hour ?? getHours(now),
    parts.minute ?? getMinutes(now),
    0,
    0,
  );
  return new CalendarDate(d, localization);
}

export function now(localization?: string): CalendarDate {
  return new CalendarDate(new Date(), localization);
}

export function isCalendarDate(value: unknown): value is CalendarDate {
  return value instanceof CalendarDate;
}

export function monthsShort(localization?: string): string[] {
  const formatter = new Intl.DateTimeFormat(localization || undefined, {
    month: "short",
  });
  return Array.from({ length: 12 }, (_, month) =>
    formatter.format(new Date(2020, month, 1)),
  );
}
